/**
 * SandboxSessionProvider — sessionStorage-only state management for sandbox mode.
 *
 * SAFETY GUARANTEES:
 * - All state lives exclusively in sessionStorage (destroyed on tab close)
 * - Never touches Redux/Context/localStorage production stores
 * - sandbox_* key prefix prevents collision with production keys
 * - No API writes — all mutations are local only
 */

import React, { createContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'sandbox_state';

const INITIAL_STATE = {
  scenario: null,        // Selected scenario ID or 'custom'
  scenarioLabel: null,   // Display name
  orgType: null,
  techStack: [],
  complianceNeeds: [],
  riskProfile: null,
  answers: {},           // Questionnaire answers
  assessments: [],       // Assessment entries
  currentStep: 0,
  analysisResult: null,  // Generated analysis
  analysisComplete: false,
  customStack: false,    // Whether user built a custom stack
};

export const SandboxContext = createContext(null);

function readState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage full or unavailable — degrade gracefully
  }
}

export default function SandboxSessionProvider({ children }) {
  const [state, setState] = useState(() => readState() || { ...INITIAL_STATE });

  // Persist to sessionStorage on every change
  useEffect(() => {
    writeState(state);
  }, [state]);

  const updateState = useCallback((updates) => {
    setState(prev => {
      const next = typeof updates === 'function' ? updates(prev) : { ...prev, ...updates };
      return next;
    });
  }, []);

  const loadScenario = useCallback((scenario) => {
    const newState = {
      ...INITIAL_STATE,
      scenario: scenario.id,
      scenarioLabel: scenario.label,
      orgType: scenario.orgType,
      techStack: scenario.techStack,
      complianceNeeds: scenario.complianceNeeds,
      riskProfile: scenario.riskProfile,
      answers: scenario.prefilledAnswers || {},
      assessments: scenario.prefilledAssessments || [],
      customStack: false,
    };
    setState(newState);
  }, []);

  const loadCustomStack = useCallback((config) => {
    const newState = {
      ...INITIAL_STATE,
      scenario: 'custom',
      scenarioLabel: 'Custom Stack',
      orgType: config.orgType,
      techStack: config.techStack,
      complianceNeeds: config.complianceNeeds,
      riskProfile: config.riskProfile,
      answers: {},
      assessments: [],
      customStack: true,
    };
    setState(newState);
  }, []);

  const updateAnswer = useCallback((questionId, value) => {
    setState(prev => ({
      ...prev,
      answers: { ...prev.answers, [questionId]: value },
    }));
  }, []);

  const updateAssessment = useCallback((pillar, data) => {
    setState(prev => {
      const assessments = [...prev.assessments];
      // Match by capKey for capability-level, fall back to pillar-level
      const matchKey = data.capKey || null;
      const existing = matchKey
        ? assessments.findIndex(a => a.capKey === matchKey)
        : assessments.findIndex(a => a.pillar === pillar && !a.capKey);
      if (existing >= 0) {
        assessments[existing] = { ...assessments[existing], ...data };
      } else {
        assessments.push({ pillar, ...data });
      }
      return { ...prev, assessments };
    });
  }, []);

  const setAnalysisResult = useCallback((result) => {
    setState(prev => ({
      ...prev,
      analysisResult: result,
      analysisComplete: true,
    }));
  }, []);

  const resetSandbox = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setState({ ...INITIAL_STATE });
  }, []);

  const value = {
    ...state,
    updateState,
    loadScenario,
    loadCustomStack,
    updateAnswer,
    updateAssessment,
    setAnalysisResult,
    resetSandbox,
  };

  return (
    <SandboxContext.Provider value={value}>
      {children}
    </SandboxContext.Provider>
  );
}
